import {
  api,
  queryKeys,
  type Withdrawal as WithdrawalRecord,
  WITHDRAWAL_MIN_LDS,
  withdrawalInput,
  type WithdrawalInput,
} from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useSession } from '../../hooks/useSession';
import { useTableQuery } from '../../hooks/useTableQuery';
import {
  formatDateTime,
  formatLds,
  formatWithdrawalAmount,
  statusTone,
  withdrawalStatusLabel,
  WITHDRAWAL_LABEL,
} from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input, StatCard, StatCards } from '../../ui';
import { WALLET_TABS } from './Deposit';

const col = createColumnHelper<WithdrawalRecord>();

/**
 * Alpha One withdrawal terms (amended 2026-08-01, client requirement): no
 * admin fee, 750 LDS flat minimum, processed 24×7. There is deliberately no
 * withdrawal-window logic here — the previous platform restricted payouts to
 * fixed weekday hours; this one does not.
 *
 * MAIN SITE: LDS-ONLY PAGE, 2026-08-01 (client requirement, Hindi original:
 * "विथड्रावल पेज में LDS टोकन hi withdraw होगा, USDT नहीं दिखाना है कहीं भी").
 * This page must never show a raw USD figure on the main build (formatUsd is
 * intentionally not imported here) — but formatWithdrawalAmount's label IS
 * build-configurable (VITE_WITHDRAWAL_LABEL), and the 2026-09-11 LDS1
 * subdomain build deliberately sets it to "USDT" per that deployment's own,
 * separate client requirement ("Withdrawal ONLY USDT SE DIYA JAYE") — this is
 * NOT a contradiction of the 2026-08-01 rule, it's a different deployment.
 */
export function Withdrawal() {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const { query, setPage } = useTableQuery();

  const wallet = useQuery({
    queryKey: queryKeys.wallet(),
    queryFn: () => api.wallet.balances().then((r) => r.data),
  });

  const history = useQuery({
    queryKey: queryKeys.withdrawals(query),
    queryFn: () => api.withdrawals.list(query),
  });

  const form = useForm<WithdrawalInput>({
    resolver: zodResolver(withdrawalInput),
    defaultValues: { amount_lds: WITHDRAWAL_MIN_LDS, transaction_pin: '' },
  });

  const amount = form.watch('amount_lds');

  const quote = useQuery({
    queryKey: ['withdrawal-quote', amount],
    queryFn: () => api.withdrawals.quote(Number(amount)).then((r) => r.data),
    enabled: Number(amount) >= WITHDRAWAL_MIN_LDS,
  });

  const submit = useMutation({
    mutationFn: (values: WithdrawalInput) => api.withdrawals.create(values),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet() });
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
  });

  const columns = useMemo(
    () => [
      // LDS-only history table (2026-08-01): amount_usd/net_usd exist on the
      // wire type for backward compatibility with historical records (every
      // withdrawal stores amount_usd internally, same as every other income
      // type on this platform), but this page never renders a USD figure —
      // amount_lds is what was actually requested/paid.
      col.accessor('amount_lds', { header: 'Requested', cell: (i) => formatWithdrawalAmount(i.getValue()) }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(String(i.getValue()))}>{withdrawalStatusLabel(String(i.getValue()))}</Chip>,
      }),
      col.accessor('tx_hash', {
        header: 'Tx Hash',
        cell: (i) => {
          const hash = i.getValue();
          if (!hash) return '—';
          return (
            <a
              href={`https://bscscan.com/tx/${hash}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View on BscScan"
            >
              {hash.slice(0, 12)}…
            </a>
          );
        },
      }),
      col.accessor('created_at', { header: 'Requested at', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Withdrawal"
        description={`No admin fee · ${WITHDRAWAL_MIN_LDS} ${WITHDRAWAL_LABEL} minimum · processed 24×7`}
        tabs={WALLET_TABS}
      />

      <StatCards>
        <StatCard label="Income Wallet" value={formatLds(wallet.data?.income_balance_lds)} />
        <StatCard label="Total Withdrawn" value={formatWithdrawalAmount(wallet.data?.total_withdrawn_lds)} />
      </StatCards>

      <Card>
        {submit.isSuccess ? <Alert tone="success">Withdrawal requested.</Alert> : null}
        {submit.error ? <Alert tone="danger">{(submit.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => submit.mutate(values))} noValidate>
          <FormGroup
            label={`Amount (${WITHDRAWAL_LABEL})`}
            error={form.formState.errors.amount_lds?.message}
            hint={`Minimum ${WITHDRAWAL_MIN_LDS} ${WITHDRAWAL_LABEL}`}
          >
            <Input type="number" step="1" min={WITHDRAWAL_MIN_LDS} {...form.register('amount_lds')} />
          </FormGroup>

          {quote.data ? (
            <dl className="quote-list">
              <div>
                <dt>Admin fee</dt>
                <dd>
                  {Number(quote.data.fee_lds) > 0
                    ? formatWithdrawalAmount(quote.data.fee_lds)
                    : `0 ${WITHDRAWAL_LABEL} (no charge)`}
                </dd>
              </div>
              <div>
                <dt>You receive</dt>
                <dd>{formatWithdrawalAmount(quote.data.net_lds)}</dd>
              </div>
            </dl>
          ) : null}

          <FormGroup label="Payout Wallet" hint="Withdrawals always pay out to your registered login wallet">
            <Input value={user?.wallet_address ?? ''} readOnly disabled />
          </FormGroup>

          <FormGroup label="Transaction PIN" error={form.formState.errors.transaction_pin?.message}>
            <Input type="password" {...form.register('transaction_pin')} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={submit.isPending}>
            {submit.isPending ? 'Requesting…' : 'Request Withdrawal'}
          </Button>
        </form>
      </Card>

      <DataTable<WithdrawalRecord>
        title="Withdrawal History"
        columns={columns}
        data={history.data?.data}
        meta={history.data?.meta}
        isLoading={history.isLoading}
        error={history.error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
